import {BrowserModule} from '@angular/platform-browser';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {EnvironmentProviders, Injectable, NgModule, Provider, provideZoneChangeDetection, Type} from '@angular/core';
import {
    HTTP_INTERCEPTORS,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    provideHttpClient,
    withInterceptorsFromDi
} from '@angular/common/http';

import {AppComponent} from './app.component';
import {LoginComponent} from './public/login/login.component';
import {RegisterAnonymousUserComponent} from './public/register-anonymous-user/register-anonymous-user.component';
import {AuthenticationService} from './sdk/service/authentication.service';
import {AlertModule} from './sdk/component/alert/alert.module';
import {HomeComponent} from './secure/home/home.component';
import {EcommerceLandingComponent} from './secure/ecommerce-landing/ecommerce-landing.component';
import {VerificationCardComponent} from './secure/ecommerce-landing/verification-card/verification-card.component';
import {EcommerceKycWizardComponent} from './secure/ecommerce-kyc/ecommerce-kyc-wizard.component';
import {KycStep1Component} from './secure/ecommerce-kyc/kyc-step1/kyc-step1.component';
import {UIRoutes} from './app.routing';
import {LayoutModule} from './secure/layout/layout.module';
import {QRCodeComponent} from 'angularx-qrcode';
import {UserListComponent} from './secure/user/user-list/user-list.component';
import {UserInvitationComponent} from './secure/user/user-invitation/user-invitation.component';
import {UserEditComponent} from './secure/user/user-edit/user-edit.component';
import {SearchViewComponent} from './sdk/component/search/search-view/search-view.component';
import {SignUpComponent} from './public/signup/sign-up.component';
import {VerifyEmailComponent} from './public/signup/verify-email/verify-email.component';
import {NgIdleKeepaliveModule} from '@ng-idle/keepalive';
import {AccountSettingsComponent} from './secure/account-settings/account-settings.component';
import {PaymentRequestListComponent} from './secure/payment/payment-request-list/payment-request-list.component';
import {PaymentRequestCreateComponent} from './secure/payment/payment-request-create/payment-request-create.component';
import {PaymentRequestViewComponent} from './secure/payment/payment-request-view/payment-request-view.component';
import {PaymentRequestSendComponent} from './secure/payment/payment-request-send/payment-request-send.component';
import {PaymentRequestEditComponent} from './secure/payment/payment-request-edit/payment-request-edit.component';
import {PasswordResetComponent} from './public/password-reset/password-reset.component';
import {AccountRecoveryComponent} from './public/account-recovery/account-recovery.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {ApiModule, Configuration, ConfigurationParameters} from 'web-angular-client';
import {MomentModule} from 'ngx-moment';
import {BatchListComponent} from './secure/settlement/batch/batch-list/batch-list.component';
import {BatchViewComponent} from './secure/settlement/batch/batch-view/batch-view.component';
import {RoleGuardService} from './sdk/service/role-guard.service';
import {AuthGuardService} from './sdk/service/auth-guard.service';
import {EcommerceGuardService} from './sdk/service/ecommerce-guard.service';
import {UserInvitationListComponent} from './secure/user/user-invitation-list/user-invitation-list.component';
import {RoleSelectorComponent} from './sdk/component/role/role-selector/role-selector.component';
import {ConfirmationComponent} from './sdk/component/confirmation/confirmation.component';
import {RefundDialogComponent} from './sdk/component/refund-dialog/refund-dialog.component';
import {ConfirmationService} from './sdk/service/confirmation.service';
import {AccountStatusPipe} from './sdk/pipe/accountStatus.pipe';
import {BatchStatusPipe} from './sdk/pipe/batchStatus.pipe';
import {CancelPaymentService} from './secure/payment/cancel-payment/cancel-payment.service';
import {CancelPaymentComponent} from './secure/payment/cancel-payment/cancel-payment.component';
import {ToMajorPipe} from './sdk/pipe/to-major.pipe';
import {PaymentRequestFormComponent} from './sdk/component/payment-request-form/payment-request-form.component';
import {CheckboxModule} from 'primeng/checkbox';
import {FileUploadModule} from 'primeng/fileupload';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {StepsModule} from 'primeng/steps';
import {NgxUiLoaderConfig, NgxUiLoaderModule, POSITION, SPINNER} from 'ngx-ui-loader';
import {SignupStep1FullComponent} from './public/signup/signup-step1/signup-step1-full.component';
import {SignupStep1TrimmedComponent} from './public/signup/signup-step1/signup-step1-trimmed.component';
import {PaymentSearchComponent} from './secure/payment/payment-search/payment-search.component';
import {UserSearchComponent} from './secure/user/user-search/user-search.component';
import {UserInvitationSearchComponent} from './secure/user/user-invitation-search/user-invitation-search.component';
import {BatchSearchComponent} from './secure/settlement/batch/batch-search/batch-search.component';
import {environment} from '../environments/environment';
import {OnlyNumbersDirective} from './sdk/directives/only-numbers.directive';
import {BulkPaymentRequestComponent} from './secure/payment/bulk-payment-request/bulk-payment-request.component';
import {AccountNumberSansMerchantPrefixPipe} from './sdk/pipe/account-number-sans-merchant-prefix.pipe';
import {BulkPaymentSearchComponent} from './secure/payment/bulk-payment-search/bulk-payment-search.component';
import {PaymentRequestUploadComponent} from './secure/payment/payment-request-upload/payment-request-upload.component';
import {EcommercePaymentDashboardComponent} from './secure/payment/ecommerce-payment-dashboard/ecommerce-payment-dashboard.component';
import {EcommerceDashboardComponent} from './secure/ecommerce/dashboard/ecommerce-dashboard.component';
import {RefundDashboardComponent} from './secure/ecommerce/refund-dashboard/refund-dashboard.component';
import {BalanceTileComponent} from './secure/ecommerce/dashboard/balance-tile/balance-tile.component';
import {StatTileComponent} from './secure/ecommerce/dashboard/stat-tile/stat-tile.component';
import {InfoTipDirective} from './secure/ecommerce/dashboard/info-tip.directive';
import {EcommerceAccountTableComponent} from './secure/ecommerce/account-table/ecommerce-account-table.component';
import {RefundTransactionsComponent} from './secure/payment/refund-transactions/refund-transactions.component';
import {RefundTableComponent} from './secure/refund/refund-table/refund-table.component';
import {RefundSearchComponent} from './secure/refund/refund-search/refund-search.component';
import {FloatMonitoringComponent} from './secure/ecommerce/float-monitoring/float-monitoring.component';
import {AvailableFloatCardComponent} from './secure/ecommerce/float-monitoring/available-float-card/available-float-card.component';
import {FloatEventsComponent} from './secure/ecommerce/float-monitoring/float-events/float-events.component';
import {TopUpFloatDialogComponent} from './secure/ecommerce/float-monitoring/top-up-float-dialog/top-up-float-dialog.component';
import {InvoicesComponent} from './secure/invoices/invoices.component';
import {MessageModule} from 'primeng/message';
import {MessageBundlesComponent} from './secure/purchase/message-bundles/message-bundles.component';
import {DividerModule} from 'primeng/divider';
import {RippleModule} from 'primeng/ripple';
import {CardModule} from 'primeng/card';
import {DialogModule} from 'primeng/dialog';
import {InputTextModule} from 'primeng/inputtext';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NotificationStatusPipe} from './sdk/pipe/notificationStatusPipe';
import {TagModule} from 'primeng/tag';
import {PaginatorModule} from 'primeng/paginator';
import {QrDashboardComponent} from './secure/qr/qr.dashboard.component';
import {EditQrCodeComponent} from './secure/qr/edit-qr-code/edit-qr-code.component';
import {RadioButtonModule} from 'primeng/radiobutton';
import {QrSearchComponent} from './secure/qr/qr-search/qr-search.component';
import {QrCodeDetailsComponent} from './secure/qr/qr-code-details/qr-code-details.component';
import {QrPaymentsDashboardComponent} from './secure/qr/qr-payments-dashboard/qr-payments-dashboard.component';
import {FaqsComponent} from './public/faqs/faqs.component';
import {AccordionModule} from 'primeng/accordion';
import {NgxMaskDirective, NgxMaskPipe, provideNgxMask} from 'ngx-mask';
import {ValidationComponent} from './sdk/component/validation/validation.component';
import {AddressFormComponent} from './sdk/component/address-form/address-form.component';
import {AvsFormComponent} from './sdk/component/avs-form/avs-form.component';
import {KycFormComponent} from './sdk/component/kyc-form/kyc-form.component';
import {CurrencyMaskDirective} from './sdk/directives/currency-mask.directive';
import {AssetPipeModule} from './sdk/pipe/assetPipeModule';
import {CurrencyPipe, NgOptimizedImage} from '@angular/common';
import {InvoiceComponent} from './public/invoice/invoice.component';
import {AssetSourcePipe} from './sdk/pipe/assetSource.pipe';
import {ProductWalkthroughComponent} from './public/product-walkthrough/product-walkthrough.component';
import {PublicPageTemplateComponent} from './sdk/component/public-page-template/public-page-template.component';
import {RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module} from 'ng-recaptcha-2';
import {Select} from 'primeng/select';
import {SelectButton} from 'primeng/selectbutton';
import {providePrimeNG} from 'primeng/config';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import Aura from '@primeuix/themes/aura';
import {ContextMenuModule} from 'primeng/contextmenu';
import {MenuModule} from 'primeng/menu';
import {FeesComponent} from './secure/fees/fees.component';
import {FilterPersistenceService} from './sdk/component/search/filter-persistence.service';
import {Tooltip} from 'primeng/tooltip';
import {ResetMerchantClientSecretComponent} from './secure/reset-merchant-client-secret/reset-merchant-client-secret.component';
import {DialogService} from 'primeng/dynamicdialog';
import {ButtonDirective, ButtonIcon, ButtonLabel} from 'primeng/button';
import {NormaliseRegistrationNumberDirective} from './sdk/directives/normalise-registration-number.directive';
import {TableModule} from 'primeng/table';
import {AuthErrorInterceptor} from './sdk/service/auth-error.interceptor';

@Injectable()
export class XhrInterceptor implements HttpInterceptor
{
    intercept(req: HttpRequest<any>, next: HttpHandler)
    {
        const xhr = req.clone({
            headers: req.headers.set('X-Requested-With', 'XMLHttpRequest')
        });
        return next.handle(xhr);
    }
}

export const apiConfigurationParams: ConfigurationParameters = {
    withCredentials: false,
    basePath: 'v1'
};

export const apiConfig = new Configuration(apiConfigurationParams);

export function getApiConfig()
{
    return apiConfig;
}

/** default configuration used with the UI loading screen */
const ngxUiLoaderConfig: NgxUiLoaderConfig = {
    fgsSize: 60,
    fgsPosition: POSITION.bottomCenter,
    bgsOpacity: 0.5,
    bgsPosition: POSITION.bottomCenter,
    bgsSize: 60,
    bgsType: SPINNER.ballSpinClockwise,
    blur: 5,
    text: 'Loading...',
    textColor: '#FFFFFF',
    textPosition: POSITION.centerCenter
};

declare module '@angular/core'
{
    interface ModuleWithProviders<T = any>
    {
        ngModule: Type<T>;
        providers?: (Provider | EnvironmentProviders)[];
    }
}

@NgModule({
    declarations: [
        AppComponent,
        PublicPageTemplateComponent,
        LoginComponent,
        RegisterAnonymousUserComponent,
        HomeComponent,
        EcommerceLandingComponent,
        VerificationCardComponent,
        EcommerceKycWizardComponent,
        KycStep1Component,
        UserListComponent,
        UserInvitationComponent,
        UserEditComponent,
        SearchViewComponent,
        SignUpComponent,
        AccountSettingsComponent,
        PaymentRequestListComponent,
        PaymentRequestCreateComponent,
        PaymentRequestViewComponent,
        PaymentRequestSendComponent,
        PaymentRequestEditComponent,
        PaymentRequestUploadComponent,
        EcommercePaymentDashboardComponent,
        EcommerceDashboardComponent,
        RefundDashboardComponent,
        BalanceTileComponent,
        StatTileComponent,
        InfoTipDirective,
        FloatMonitoringComponent,
        AvailableFloatCardComponent,
        FloatEventsComponent,
        TopUpFloatDialogComponent,
        EcommerceAccountTableComponent,
        RefundTransactionsComponent,
        RefundTableComponent,
        RefundSearchComponent,
        PasswordResetComponent,
        ResetMerchantClientSecretComponent,
        AccountRecoveryComponent,
        BatchListComponent,
        BatchViewComponent,
        UserInvitationListComponent,
        RoleSelectorComponent,
        ConfirmationComponent,
        RefundDialogComponent,
        AccountStatusPipe,
        BatchStatusPipe,
        NotificationStatusPipe,
        CancelPaymentComponent,
        ToMajorPipe,
        AccountNumberSansMerchantPrefixPipe,
        PaymentRequestFormComponent,
        SignupStep1FullComponent,
        SignupStep1TrimmedComponent,
        VerifyEmailComponent,
        PaymentSearchComponent,
        UserSearchComponent,
        UserInvitationSearchComponent,
        BatchSearchComponent,
        BulkPaymentRequestComponent,
        BulkPaymentSearchComponent,
        OnlyNumbersDirective,
        BatchSearchComponent,
        InvoicesComponent,
        MessageBundlesComponent,
        QrDashboardComponent,
        EditQrCodeComponent,
        QrSearchComponent,
        QrCodeDetailsComponent,
        QrPaymentsDashboardComponent,
        FaqsComponent,
        ValidationComponent,
        AddressFormComponent,
        AvsFormComponent,
        KycFormComponent,
        InvoiceComponent,
        FeesComponent,
        NormaliseRegistrationNumberDirective
    ],
    bootstrap: [AppComponent], imports: [
        Select,
        SelectButton,
        ToggleSwitch,
        BrowserModule,
        ReactiveFormsModule,
        ApiModule.forRoot(getApiConfig),
        NgbModule,
        NgIdleKeepaliveModule.forRoot(),
        UIRoutes,
        AlertModule,
        LayoutModule,
        QRCodeComponent,
        StepsModule,
        CheckboxModule,
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        FileUploadModule,
        FormsModule,
        RecaptchaV3Module,
        MessageModule,
        DividerModule,
        RippleModule,
        CardModule,
        DialogModule,
        InputTextModule,
        BrowserAnimationsModule,
        TagModule,
        PaginatorModule,
        RadioButtonModule,
        AccordionModule,
        NgxMaskDirective,
        NgxMaskPipe,
        CurrencyMaskDirective,
        MomentModule.forRoot(),
        AssetPipeModule,
        NgOptimizedImage,
        ProductWalkthroughComponent,
        MessageModule,
        ContextMenuModule,
        MenuModule,
        Tooltip,
        ButtonDirective,
        ButtonIcon,
        ButtonLabel,
        TableModule
    ],
    exports: [
        ValidationComponent
    ],
    providers: [
        provideZoneChangeDetection({eventCoalescing: true}),
        AssetSourcePipe,
        FileUploadModule,
        ToMajorPipe,
        CurrencyPipe,
        CancelPaymentService,
        DialogService,
        ConfirmationService,
        RoleGuardService,
        AuthGuardService,
        EcommerceGuardService,
        AuthenticationService,
        AccountNumberSansMerchantPrefixPipe,
        FilterPersistenceService,
        {provide: HTTP_INTERCEPTORS, useClass: XhrInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true},
        {provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha.siteKey},
        provideNgxMask(),
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    darkModeSelector: 'none'
                }
            }
        })
    ]
})
export class AppModule
{
}
